import { AfterViewInit, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CtBinaryOperator,
  CtDatatableContainerService,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelOperator
} from '@ctsolution/ct-framework';
import { WorkOrderPaymentRouteData } from '../../../../../_core/route-data/work-order-payment.route-data';
import { CTMGeneralService } from '../../../../../_core/lib/general.service';
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../../../_core/constants';
import { PaymentsService } from '../../../../payments/payments.service';
import { PaymentsEditConfiguration } from '../../../../payments/payments-edit/payments-edit.configuration';

/**
 * Tab "Fatturazione" del work-order-edit.
 *
 * Mostra i record di WorkOrderPayment generati automaticamente o inseriti
 * manualmente per il work order corrente, filtrando la datatable per
 * `WorkOrder.Oid`. Consente anche l'inserimento manuale di nuovi pagamenti
 * associati al work-order corrente, tramite il dialog di `PaymentsEditComponent`
 * con `WorkOrderOid` pre-valorizzato.
 *
 * Pattern: analogo a `WorkOrderTaskComponent` (riuso del controller `workorderpayment`
 * via `<ct-model>` con `WorkOrderPaymentRouteData()` + filtro costante).
 */

/**
 * Operazioni abilitate sulla datatable del tab Fatturazione.
 *
 * Include `Create`: l'inserimento manuale di un pagamento associato al work-order
 * corrente avviene da qui, aprendo il dialog di `PaymentsEditComponent` con
 * `WorkOrderOid` pre-valorizzato (vedi `openCreateDialog`).
 *
 * `Detail` è volutamente ESCLUSO: la datatable globale `payments-list` usa il
 * Detail per navigare al work-order del pagamento; qui saremmo già dentro lo
 * stesso work-order, quindi il Detail genererebbe una navigazione ricorsiva /
 * un loop nello stato dell'edit. La visualizzazione del pagamento avviene
 * comunque tramite Update (che apre la stessa form).
 *
 * ⚠️ Esposto come FACTORY (non come costante array) perché il merge della
 * configurazione caricata dal backend (`CtModelService.getFields` →
 * `CtModelConfiguration.setupCTModelConfig` → `CTBase.getClass`) effettua un
 * `push` in-place sull'array `Operations`. Senza una factory che restituisca un
 * array fresco a ogni chiamata, il backend potrebbe inquinare permanentemente la
 * lista.
 */
const tabOperations = (): CtModelOperator[] => [
  CtModelOperator.Update,
  CtModelOperator.Create
];

@Component({
  selector: 'ctm-work-order-payments',
  templateUrl: './work-order-payments.component.html',
  styleUrls: ['./work-order-payments.component.scss']
})
export class WorkOrderPaymentsComponent implements AfterViewInit {

  configuration: CtModelConfiguration<WorkOrderPaymentsComponent> | null = null;

  /**
   * Oid della Company del work-order corrente, passato dal container padre
   * (`WorkOrderDefaultEditComponent`) via `[companyOid]="viewModel.workOrder?.Company?.Oid"`.
   *
   * Viene propagato al dialog di Create attraverso `PaymentsEditConfiguration.CompanyOid`,
   * così `PaymentsEditComponent.submit()` può settarlo nel payload anche quando il
   * campo `Company` è stato rimosso dal form (caso work-order). Nessuna nuova
   * chiamata API: l'oggetto WorkOrder è già caricato in pagina dal padre.
   */
  @Input() companyOid: number | null = null;

  /**
   * Template custom della cella "Description".
   * Definito inline in work-order-payments.component.html e attaccato alla colonna
   * in `setup()` tramite `setFieldsCustomTemplates([...])`.
   * Necessario per onorare i `\r\n` presenti nei valori restituiti dal backend
   * (vedi `white-space: pre-wrap` nel template).
   */
  @ViewChild("descriptionCellTpl", { static: true }) descriptionCellTpl: TemplateRef<any> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private general: CTMGeneralService,
    private paymentsService: PaymentsService,
    private datatableContainerService: CtDatatableContainerService
  ) { }

  // Mantengo l'OID del work-order corrente come stato del componente:
  // serve sia per il filtro costante sia per il payload del dialog di Create.
  private workOrderOid: number | null = null;

  ngAfterViewInit(): void {

    const pId = this.route.snapshot.paramMap.get("id");

    if (!pId) return;

    // Ritardiamo il setup con setTimeout per evitare ExpressionChangedAfterItHasBeenCheckedError
    // (stesso accorgimento usato in WorkOrderTaskComponent).
    setTimeout(() => this.setup(+pId), 0);

  }

  private setup(workOrderOid: number): void {

    this.workOrderOid = workOrderOid;

    // Filtro costante: solo i pagamenti collegati al work order corrente.
    const workOrderFilter: CTModelDatatableFilter = CTModelDatatableFilter
      .create()
      .setField("WorkOrder.Oid")
      .setValue(workOrderOid)
      .setOperatorType(CtBinaryOperator.Equal);

    this.configuration = CtModelConfiguration
      .create<WorkOrderPaymentsComponent>()
      .setRouteData(WorkOrderPaymentRouteData())
      .setOperations(tabOperations())
      // `rowHeight: 'auto'` per onorare il `white-space: pre-wrap` della cella Description:
      // il valore arriva dal backend con `\r\n` e quindi può essere multi-riga; col
      // valore fisso di `setRowHeight(60)` ereditato da DEFAULT_DATATABLE_CONFIGURATION le
      // righe successive alla prima venivano clippate.
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setRowHeight('auto'))
      // L'edit apre il dialog di PaymentsEditComponent (NON naviga alla route
      // `/payments/edit/:id`): così restiamo sulla pagina del work-order, il back
      // del browser torna correttamente alla list/dettaglio offerta invece che alla
      // lista globale dei pagamenti. Il dialog riceve l'Oid del record da editare
      // oltre a WorkOrderOid e CompanyOid del contesto work-order, in modo che il
      // form si comporti uniformemente sia in Create che in Update (Company/WorkOrder
      // rimossi dal form, valori dedotti dal contesto).
      .setOnEdit(event => this.openEditDialog(event?.Oid))
      // Create: apre lo stesso dialog senza Oid (nuovo record), con WorkOrderOid
      // pre-valorizzato. PaymentsEditComponent rimuove automaticamente il campo
      // Company (deriva dal work-order lato backend).
      .setOnCreate(() => this.openEditDialog(null));

    // Aggancia il template custom per la cella "Description" (multilinea con \r\n).
    // Il framework, all'interno di `setupCTDatatableConfigurationFromCTModel`, fa
    // `{...clm, ...columnCustomTemplate}` su ciascuna colonna che matcha `prop`,
    // applicando di fatto il nostro `cellTemplate` alla colonna `Description`.
    if (this.descriptionCellTpl) {
      this.configuration.setFieldsCustomTemplates([
        { prop: "Description", cellTemplate: this.descriptionCellTpl }
      ]);
    }

    // Applica il filtro costante per il work order corrente.
    this.configuration
      .filterValues
      .addConstantFilter(workOrderFilter);

    // Ripristina le Operations DOPO che la configurazione è stata fusa col backend.
    // Il JSON di configuration del controller `workorderpayment` include `Create` tra
    // le sue Operations e `CTBase.getClass` la aggiunge in-place al nostro array: senza
    // questo override il bottone "Crea" comparirebbe nella toolbar (in particolare era
    // visibile a lista vuota, dove la toolbar viene renderizzata subito).
    //
    // NOTA: passiamo SEMPRE un nuovo array (factory) per non condividere il riferimento
    // con strutture esterne che il framework potrebbe nuovamente mutare.
    this.configuration
      .initialized()
      .subscribe(() => this.configuration?.setOperations(tabOperations()));

  }

  /**
   * Apre il dialog di `PaymentsEditComponent` per il work-order corrente.
   * - Se `oid` è valorizzato → modalità Edit (il componente carica il record esistente
   *   via `PaymentsEditConfiguration.Oid`).
   * - Se `oid` è null → modalità Create.
   *
   * In entrambi i casi vengono propagati `WorkOrderOid` e (se disponibile) `CompanyOid`
   * del work-order corrente, così `applyFormControlsConfiguration` rimuove Company/WorkOrder
   * dal form e `submit()` valorizza il payload coi dati del contesto.
   *
   * A chiusura ricarica la datatable tramite il canale ufficiale del framework
   * (`CtDatatableContainerService.refreshData(controller)`).
   *
   * NOTA: `PaymentsEditComponent.submit()` esegue ancora `window.location.reload()`
   * dopo `dialogRef.close()`. Finché quel reload è in piedi, il refreshData qui
   * sotto è di fatto vanificato (la pagina si ricarica comunque). Da risolvere in
   * un intervento dedicato sull'edit.
   */
  private openEditDialog(oid: number | null): void {

    if (!this.workOrderOid) return;

    const param = PaymentsEditConfiguration
      .create()
      .setWorkOrderOid(this.workOrderOid);

    // Edit di un record esistente: passiamo l'Oid al dialog.
    if (oid) {
      param.setOid(oid);
    }

    // Se disponibile il Company.Oid del work-order (Input dal padre), lo propaghiamo
    // al dialog: serve a PaymentsEditComponent.submit() per popolare Company nel
    // payload quando il control è stato rimosso dal form (caso work-order).
    if (this.companyOid) {
      param.setCompanyOid(this.companyOid);
    }

    this.paymentsService
      .openForm(param)
      .then(() => this.datatableContainerService.refreshData(
        this.configuration?.RouteData?.controller ?? null
      ));

  }

}
